// Reorder report (BUILD_SPEC.md §13, §8.6: "Reorder report: on-hand vs min level
// -> suggested PO lines grouped by supplier -> one-click draft POs"). Plan.md G20.
//
// The reporting endpoint (`GET /reporting/reorder`, G19) already resolves each
// below-min product's "preferred supplier" (its most-recently-effective ACTIVE
// supplier_contracts row) but only returns the supplier id/name — not the actual
// price/bands, since that report is generic across every module and doesn't know
// purchasing's DTO shapes. The one-click "create draft PO" button here re-fetches
// each product's active contract with that supplier (purchasing module) to get
// the pricing needed for a valid `purchase_order_lines` row, then posts one
// DRAFT PO per supplier group.
import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Select, Stack, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconShoppingCart } from '@tabler/icons-react';
import { apiFetch } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { PageHeader } from '../../components/PageHeader';
import { formatQtyDisplay } from './common';
import {
  type Page,
  type ProductRow,
  type PurchaseOrderLineWriteInput,
  type PurchaseOrderWriteInput,
  type ReorderItem,
  type SupplierContractRow,
  useCreatePurchaseOrder,
  useProducts,
  useReorderSuggestions,
  useSites,
} from './api';

interface SupplierGroup {
  supplierId: string | null;
  supplierName: string;
  items: ReorderItem[];
}

function groupBySupplier(items: ReorderItem[]): SupplierGroup[] {
  const groups = new Map<string, SupplierGroup>();
  for (const item of items) {
    const key = item.preferredSupplierId ?? '__none__';
    if (!groups.has(key)) {
      groups.set(key, {
        supplierId: item.preferredSupplierId,
        supplierName: item.preferredSupplierName ?? 'No supplier configured',
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }
  return [...groups.values()];
}

/** tonnes with up to 3dp, matching `purchaseOrderLineInputSchema.qtyTonnes`. */
function kgToTonnes(kg: string): string {
  return (Number(kg) / 1000).toFixed(3);
}

function SupplierGroupCard({ group, siteId }: { group: SupplierGroup; siteId: string | null }) {
  const canOperate = useCanOperate();
  const createPo = useCreatePurchaseOrder();
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  async function handleCreateDraftPo() {
    if (!group.supplierId || !siteId) return;
    setError(null);
    setCreated(null);

    const lines: PurchaseOrderLineWriteInput[] = [];
    const skipped: string[] = [];

    for (const [index, item] of group.items.entries()) {
      try {
        const contracts = await apiFetch<Page<SupplierContractRow>>('/purchasing/supplier-contracts', {
          query: { supplierId: group.supplierId, productId: item.productId, isActive: true, limit: 50 },
        });
        const contract = [...contracts.items].sort((a, b) => (a.validFrom < b.validFrom ? 1 : -1))[0];
        const product = await apiFetch<ProductRow>(`/config/products/${item.productId}`);
        if (!contract || !item.suggestedQty) {
          skipped.push(item.productName);
          continue;
        }
        // `PurchaseOrderLineInput`'s zod schema is now a union of a mass line
        // (`qtyTonnes`+`pricingBasis`) and an EACH count line (`qty`+
        // `priceCentsPerUnit`) — see @zebra/core/schemas/purchasing.ts. `Omit`
        // does not distribute over a union (`keyof (A|B)` is the intersection of
        // each member's own keys, a well-known TS gotcha), so
        // `PurchaseOrderLineWriteInput` (this file's `./api`, built via `Omit`)
        // ends up WITHOUT `qtyTonnes`/`pricingBasis` at all, not merely optional.
        // This report only ever builds mass (tonnes) lines today — EACH-product
        // reorder suggestions are skipped below via `!item.suggestedQty` until a
        // later goal (G-WebA) gives this report real EACH support and widens the
        // shared write type properly. The cast documents that gap without
        // changing runtime behaviour: the object literal is exactly the mass
        // variant's shape.
        lines.push({
          lineNo: index + 1,
          productId: item.productId,
          qtyTonnes: kgToTonnes(item.suggestedQty),
          pricingBasis: contract.pricingBasis,
          priceCentsPerTonne: contract.pricingBasis === 'MOISTURE_BAND' ? null : (contract.priceCentsPerTonne ?? undefined),
          bands:
            contract.pricingBasis === 'MOISTURE_BAND'
              ? (contract.bands ?? []).map((b) => ({ mcMin: b.mcMin, mcMax: b.mcMax, priceCents: b.priceCents }))
              : null,
          vatTreatment: product.vatTreatment,
          costCentreId: null,
        } as PurchaseOrderLineWriteInput);
      } catch {
        skipped.push(item.productName);
      }
    }

    if (lines.length === 0) {
      setError('No product in this group has a resolvable active contract — add one on the Contracts tab first.');
      return;
    }

    const body: PurchaseOrderWriteInput = {
      siteId,
      supplierId: group.supplierId,
      expectedAt: null,
      deliveryLocationId: null,
      notes: 'Auto-created from the reorder report',
      currency: 'ZAR',
      lines,
    };

    createPo.mutate(body, {
      onSuccess: (po) => {
        setCreated(skipped.length > 0 ? `${po.number} (skipped: ${skipped.join(', ')})` : po.number);
      },
      onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create draft PO'),
    });
  }

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={4}>{group.supplierName}</Title>
          {group.supplierId && (
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button
                  size="xs"
                  leftSection={<IconShoppingCart size={14} />}
                  loading={createPo.isPending}
                  disabled={!canOperate || !siteId}
                  onClick={handleCreateDraftPo}
                >
                  Create draft PO
                </Button>
              </span>
            </Tooltip>
          )}
        </Group>
        {!group.supplierId && (
          <Text size="xs" c="dimmed">
            Configure a supplier contract for these products to enable one-click PO creation.
          </Text>
        )}
        {group.supplierId && !siteId && (
          <Text size="xs" c="dimmed">
            Select a site above to enable one-click PO creation.
          </Text>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}
        {created && (
          <Alert color="green" icon={<IconShoppingCart size={16} />}>
            Draft PO {created} created.
          </Alert>
        )}
        <Table.ScrollContainer minWidth={640}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>On hand</Table.Th>
                <Table.Th>Min level</Table.Th>
                <Table.Th>Suggested qty</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {group.items.map((item) => (
                <Table.Tr key={item.productId}>
                  <Table.Td>
                    {item.productSku} — {item.productName}
                  </Table.Td>
                  <Table.Td>
                    <Badge color="amber" variant="light">
                      {formatQtyDisplay(item.onHandQty)} kg
                    </Badge>
                  </Table.Td>
                  <Table.Td>{formatQtyDisplay(item.minLevel)} kg</Table.Td>
                  <Table.Td>{item.suggestedQty ? `${formatQtyDisplay(item.suggestedQty)} kg` : '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Paper>
  );
}

export function ReorderReport() {
  const { data: sites } = useSites();
  const [siteId, setSiteId] = useState<string | null>(null);
  const { data, isLoading, isError } = useReorderSuggestions({ siteId: siteId ?? undefined });
  // Products list isn't consumed directly (contracts/products are resolved
  // per-group on demand), but kept warm in the cache so the PO-creation lookups
  // above resolve instantly from React Query's cache on repeat clicks.
  useProducts({ isActive: true });

  const groups = useMemo(() => groupBySupplier(data?.items ?? []), [data]);
  const siteOptions = (sites?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));

  return (
    <Stack gap="md">
      <PageHeader
        title="Reorder report"
        description="Products that have fallen below their reorder point, and how much to order."
        actions={<Select placeholder="All sites" clearable data={siteOptions} value={siteId} onChange={setSiteId} w={220} />}
      />

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load the reorder report.
        </Alert>
      )}

      {!isLoading && groups.length === 0 && (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            Nothing is below its minimum level right now.
          </Text>
        </Paper>
      )}

      {groups.map((group) => (
        <SupplierGroupCard key={group.supplierId ?? '__none__'} group={group} siteId={siteId} />
      ))}
    </Stack>
  );
}
